import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'

import ProgressDots from '../../components/progress-dots/ProgressDots'
import { useCelebration } from '../../_hooks/use-celebration'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import { isEmbeddedExperience } from '../../_utilities/embed-context'
import { QuickPassProfile } from '../../_types/quickpass-profile'
import {
  PageContainer,
  StepCard,
  StepHeading,
  StepSubheading,
  ProgressRow,
} from './styled'
import {
  ONBOARDING_STEPS,
  getInitialStepIndex,
  getStepByIndex,
  getStepKeyByIndex,
} from './helpers'
import NameStep from './steps/NameStep'
import CitiesStep from './steps/CitiesStep'
import PreferencesStep from './steps/PreferencesStep'
import PropertyTypesStep from './steps/PropertyTypesStep'
import { OnboardingStepProps } from './types'

/**
 * Onboarding wizard host. Owns the current step index, hands the active
 * step a profile snapshot + auto-save callback, advances on success,
 * navigates to /dashboard once the final step writes
 * `onboardingCompletedAt`.
 *
 * @returns {JSX.Element}
 */
const OnboardingPage = (): JSX.Element | null => {
  const navigate = useNavigate()
  const { profile, patchProfile, isProfileLoading, hasFinishedOnboarding } =
    useQuickPassProfile()
  const { celebrate } = useCelebration()

  const [stepIndex, setStepIndex] = useState<number>(0)
  const [hasSeededStep, setHasSeededStep] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  useEffect(() => {
    if (isProfileLoading === true) {
      return
    }

    if (hasFinishedOnboarding === true) {
      navigate('/dashboard', { replace: true })
    }
  }, [hasFinishedOnboarding, isProfileLoading, navigate])

  useEffect(() => {
    if (hasSeededStep === true) {
      return
    }

    if (isProfileLoading === true) {
      return
    }

    setStepIndex(getInitialStepIndex(profile))
    setHasSeededStep(true)
  }, [profile, isProfileLoading, hasSeededStep])

  const currentStep = getStepByIndex(stepIndex)
  const stepKey = getStepKeyByIndex(stepIndex)
  const isFirstStep = stepIndex === 0

  const handleStepSubmit = async (
    patch: Partial<QuickPassProfile>,
  ): Promise<void> => {
    setIsSubmitting(true)

    try {
      await patchProfile(patch)

      if (patch.onboardingCompletedAt !== undefined) {
        celebrate()
        const destination =
          isEmbeddedExperience() === true ? '/dashboard' : '/discover'
        setTimeout(() => {
          navigate(destination)
        }, 600)
        return
      }

      setStepIndex(previous =>
        Math.min(previous + 1, ONBOARDING_STEPS.length - 1),
      )
    } catch (error) {
      console.error('Failed to save onboarding step', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBack = useMemo(() => {
    if (isFirstStep === true) {
      return null
    }

    return () => {
      setStepIndex(previous => Math.max(previous - 1, 0))
    }
  }, [isFirstStep])

  if (isProfileLoading === true || hasFinishedOnboarding === true) {
    return null
  }

  const stepProps: OnboardingStepProps = {
    profile,
    onSubmit: handleStepSubmit,
    onBack: handleBack,
    isSubmitting,
  }

  return (
    <PageContainer>
      <StepCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0, transition: { duration: 0.45 } }}
      >
        <ProgressRow>
          <ProgressDots
            totalSteps={ONBOARDING_STEPS.length}
            currentStepIndex={stepIndex}
          />
        </ProgressRow>
        <StepHeading>{currentStep.title}</StepHeading>
        <StepSubheading>{currentStep.subtitle}</StepSubheading>
        <AnimatePresence mode="wait">
          <motion.div
            key={stepKey}
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.25 }}
          >
            {stepKey === 'name' ? <NameStep {...stepProps} /> : null}
            {stepKey === 'cities' ? <CitiesStep {...stepProps} /> : null}
            {stepKey === 'preferences' ? (
              <PreferencesStep {...stepProps} />
            ) : null}
            {stepKey === 'property-types' ? (
              <PropertyTypesStep {...stepProps} />
            ) : null}
          </motion.div>
        </AnimatePresence>
      </StepCard>
    </PageContainer>
  )
}

export default OnboardingPage
